'use client';

import { ArrowUpRight } from 'lucide-react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { captureException } from '@sentry/nextjs';

import { CategorySkeletonList } from '@/components/skeletons/CategorySkeleton';
import { useProductStore } from '@/presentation/hooks/useStores';

import { Button } from './ui/button';
import { Card } from './ui/card';

interface PropsType {
  showAll: boolean;
}

const Categories = observer((props: PropsType = { showAll: false }) => {
  const router = useRouter();
  const productStore = useProductStore();

  const showAll = props.showAll;
  const [displayCount, setDisplayCount] = useState(8);
  const categories = productStore.categoryHierarchy; ///.filter((x) => x.is_featured);

  const handleShowAll = () => {
    router.push('/category');
    // setShowAll(!showAll);
  };

  // Load categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        await productStore.getFeaturedCategories(!showAll);
        // Filter featured categories from the navigation data
      } catch (error) {
        captureException(error);
      }
    };

    void loadCategories();
  }, [productStore, showAll]);

  useEffect(() => {
    const handleResize = () => {
      // Adjust display count based on screen width
      if (window.innerWidth <= 768) {
        setDisplayCount(4);
      } else if (window.innerWidth < 1024) {
        setDisplayCount(6);
      } else {
        setDisplayCount(8);
      }
    };

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Call handleResize initially
    handleResize();

    // Remove event listener on component unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    // p-5 md:px-11 lg:px-20
    <section className="flex w-full flex-col gap-10 ">
      <div className="flex w-full flex-col  justify-between sm:flex-row">
        <span className="flex flex-col">
          <h3 className=" font-garnettSemiBold">Product Categories</h3>
          <h6 className="m-0 text-sm">
            Curated product categories hand-picked by motito.
          </h6>
        </span>
        <div className="nav flex">
          {/* <span className="flex gap-2">
            <button className="sliderBtn">
              <Image
                src="/assets/svgs/Icons/CaretDown.svg"
                width={16}
                height={16}
                alt="caret"
                className=" rotate-90 "
              />
            </button>
            <button className="sliderBtn">
              <Image
                src="/assets/svgs/Icons/CaretDown.svg"
                width={16}
                height={16}
                alt="caret"
                className=" -rotate-90 "
              />
            </button>
          </span> */}
          {!props.showAll && (
            <Button
              variant="ghost"
              className="p-2 text-sm font-medium"
              onClick={handleShowAll}
            >
              {showAll ? 'Show Less' : 'Show All'}
            </Button>
          )}
        </div>
      </div>
      <div className="grid h-fit grid-cols-2 gap-4 transition-all md:grid-cols-3 lg:grid-cols-4  lg:gap-y-6">
        {productStore.isLoading ? (
          <CategorySkeletonList count={8} />
        ) : (
          categories
            .slice(0, showAll ? categories.length : displayCount)
            .map((category, index) => (
              <Card
                key={category.id || index.toString()}
                className="group grid h-[168px] cursor-pointer grid-rows-12 rounded-lg border-none bg-[#EFEBF9] shadow-none transition-all hover:shadow-xl sm:h-[272px]"
                onClick={() => router.push(`/category/${category.slug}`)}
              >
                <Image
                  src={
                    category.banner_image_url || '/assets/images/no-image.png'
                  }
                  width={288}
                  height={194}
                  quality={100}
                  alt={category.name}
                  className="grid-2 row-span-8 size-full object-cover object-center"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/assets/images/no-image.png';
                  }}
                />
                <div className="row-span-4 flex h-full items-center justify-between px-3 py-2">
                  <span className=" flex flex-col justify-center text-sm">
                    <h1 className="text-sm md:text-base">{category.name}</h1>
                    <h3 className=" font-garnettSemiBold text-sm group-hover:text-motito-purple sm:text-lg md:text-xl">
                      {category.name}
                    </h3>
                  </span>
                  <span>
                    <ArrowUpRight className="hidden transition-all group-hover:block group-hover:text-motito-purple" />
                  </span>
                </div>
              </Card>
            ))
        )}
      </div>
    </section>
  );
});

Categories.displayName = 'Categories';

export default Categories;
